import { isNil, keys, cloneDeep } from "lodash";
import firebase from "firebase/app";

import firestore from "./async-firestore";

export default class GenericDB {
    constructor(collectionPath) {
        this.collectionPath = collectionPath;
    }

    /**
     * Create a document in the collection
     * @param data
     * @param id
     */
    async create(data, id = null) {
        const collectionRef = (await firestore()).collection(this.collectionPath);
        const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp();

        const dataToCreate = {
            ...data,
            createTimestamp: serverTimestamp,
            updateTimestamp: serverTimestamp
        };

        const createPromise = isNil(id) // Create doc with generated id
            ?
            collectionRef.add(dataToCreate).then(doc => doc.id) // Create doc with custom id
            :
            collectionRef
            .doc(id)
            .set(dataToCreate)
            .then(() => id);

        const docId = await createPromise;

        return {
            id: docId,
            ...data,
            createTimestamp: new Date(),
            updateTimestamp: new Date()
        };
    }

    /**
     * Read a document in the collection
     * @param id
     */
    async read(id) {
        const result = await (await firestore())
            .collection(this.collectionPath)
            .doc(id)
            .get();

        const data = result.exists ? result.data() : null;

        if (isNil(data)) return null;

        this.convertObjectTimestampPropertiesToDate(data);
        return { id, ...data };
    }

    /**
     * Read all documents in the collection following constraints
     * @param constraints
     */
    async readAll(constraints = null) {
        const collectionRef = (await firestore()).collection(this.collectionPath);
        let query = collectionRef;

        if (constraints) {
            constraints.forEach(constraint => (query = query.where(...constraint)));
        }

        const formatResult = result =>
            result.docs.map(ref =>
                this.convertObjectTimestampPropertiesToDate({
                    id: ref.id,
                    ...ref.data()
                })
            );

        return query.get().then(formatResult);
    }

    /**
     * Update a document in the collection
     * @param data
     */
    async update(data, uuid) {
        const id = data.id;
        const clonedData = cloneDeep(data);
        delete clonedData.id;

        await (await firestore())
        .collection(this.collectionPath)
            .doc(uuid)
            .update({
                ...clonedData,
                updateTimestamp: firebase.firestore.FieldValue.serverTimestamp()
            });

        return id;
    }

    /**
     * Update a document in the collection
     * @param data
     */
    async updateInside(data, path, userId) {
        const clonedData = cloneDeep(data);
        delete clonedData.id;

        await (await firestore())
        .collection(this.collectionPath)
            .doc(userId)
            .update({
                [path]: clonedData,
                updateTimestamp: firebase.firestore.FieldValue.serverTimestamp()
            });
    }

    /**
     * Delete a document in the collection
     * @param id
     */
    async delete(id) {
        return (await firestore())
            .collection(this.collectionPath)
            .doc(id)
            .delete();
    }

    /**
     * Delete all documents in the collection
     * @param id
     */
    async deleteAll() {
        return (await firestore())
            .collection(this.collectionPath)
            .where("view", "==", true)
            .get()
            .then(async querySnapshot => {
                // Once we get the results, begin a batch
                var batch = (await firestore()).batch();

                querySnapshot.forEach(doc => {
                    // For each doc, add a delete operation to the batch
                    batch.delete(doc.ref);
                });

                // Commit the batch
                return batch.commit();
            })
            .then(() => {
                // Delete completed!
                // ...
            });
    }

    /**
     * activate all documents in the collection
     * @param id
     */
    async putAllToActive() {
        return (await firestore())
            .collection(this.collectionPath)
            .where("view", "==", true)
            .get()
            .then(async querySnapshot => {
                // Once we get the results, begin a batch
                var batch = (await firestore()).batch();

                querySnapshot.forEach(async doc => {
                    await (await firestore())
                    .collection(this.collectionPath)
                        .doc(doc.id)
                        .update({
                            active: true
                        });
                });

                // Commit the batch
                return batch.commit();
            })
            .then(() => {
                // Delete completed!
                // ...
            });
    }

    /**
     * activate all documents in the collection
     * @param id
     */
    async putAllToDeActive() {
        return (await firestore())
            .collection(this.collectionPath)
            .where("view", "==", true)
            .get()
            .then(async querySnapshot => {
                // Once we get the results, begin a batch
                var batch = (await firestore()).batch();

                querySnapshot.forEach(async doc => {
                    await (await firestore())
                    .collection(this.collectionPath)
                        .doc(doc.id)
                        .update({
                            active: false
                        });
                });

                // Commit the batch
                return batch.commit();
            })
            .then(() => {
                // Delete completed!
                // ...
            });
    }

    /**
     * Convert all object Timestamp properties to date
     * @param obj
     */
    convertObjectTimestampPropertiesToDate(obj) {
        keys(obj)
            .filter(prop => obj[prop] instanceof Object)
            .forEach(prop =>
                obj[prop] instanceof firebase.firestore.Timestamp ?
                (obj[prop] = obj[prop].toDate()) :
                this.convertObjectTimestampPropertiesToDate(obj[prop])
            );
        return obj;
    }
}